import { Capacitor } from '@capacitor/core'
import { BRIAN_TOPIC_IDS } from '@/lib/topicPhotos'

/**
 * Picture matching: a port of Brian's libmatch.js (app-prototype/after, Oct 17 to
 * Oct 29 2026), same algorithm and numbers, with our topic ids.
 *
 * Every library picture carries tags written by looking at it (picLibrary.json:
 * kw, fits, avoid, shows). A story is matched on concepts, not a stray shared word:
 *  - the story's words (and its `see` list, when given) are stemmed and compared
 *    with each picture's keywords (strong), its "fits" situations (medium) and its
 *    caption (weak);
 *  - rare words count more than common ones (idf), so "opening" or "price" can't
 *    carry a match;
 *  - a picture whose "avoid" list matches the story is never used;
 *  - any soft topic's picture can serve, the story's own topic gets a lift;
 *  - below the confidence floor there is no picture: the card shows its topic
 *    photo. A wrong picture is worse than none.
 *
 * The library (about 440 KB of tags) loads lazily: call loadPicLibrary(); until
 * it arrives pickPhoto and rankPhotos return null / [].
 */

export interface LibPic {
  id: string
  /** Our topic id (real-estate-market, food-drink, ...). */
  topic: string
  subtopic: string
  tone: string
  alt: string
  shows: string
  kw: string[]
  fits: string[]
  avoid: string[]
  season: string
  /** 'process': a civic or politics story may take it. 'mood': a food or restaurant story may. */
  safe?: 'process' | 'mood'
}

/** A story as the matcher reads it (Brian's field names in brackets). */
export interface PicStory {
  /** Headline or story id [id]. */
  id?: string | null
  /** Our topic id; Brian's short ids are accepted too. */
  topic?: string | null
  /** The headline [hl]. */
  headline?: string | null
  /** The summary [dek]. */
  summary?: string | null
  angle?: string | null
  /** The topic's label [label]. */
  label?: string | null
  /** A photo-search line [search]. */
  search?: string | null
  /** What the picture should show [see]. */
  see?: string[] | null
}

export interface PicMatch {
  id: string
  url: string
  own?: boolean
  safe: 'process' | 'mood' | null
  score: number
  hits: string[]
  sharp?: boolean
  named?: boolean
  tie?: number
}

// ---------- the photos' home ----------

const HOSTED = 'https://gogrov-dev.web.app'

/**
 * A library photo's URL. The website serves them from Firebase hosting
 * (public/library); the iPhone app does not carry them (57 MB), so inside the
 * native app they come from the hosted site.
 */
export function libraryUrl(id: string): string {
  const path = `/library/${id}.jpg`
  return Capacitor.isNativePlatform() ? HOSTED + path : path
}

// ---------- loading ----------

let LIB: LibPic[] | null = null
let loading: Promise<LibPic[]> | null = null
const listeners = new Set<() => void>()

/** Loads the library once (a separate chunk). Resolves with the pictures. */
export function loadPicLibrary(): Promise<LibPic[]> {
  if (LIB) return Promise.resolve(LIB)
  if (!loading) {
    loading = import('./picLibrary.json').then((m) => {
      LIB = (m.default ?? m) as unknown as LibPic[]
      IX = null
      listeners.forEach((fn) => fn())
      return LIB
    }).catch((e) => { loading = null; throw e })
  }
  return loading
}

export const picLibraryReady = () => LIB !== null
export function onPicLibrary(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn) } }
/** The loaded library (empty until loadPicLibrary resolves). */
export const picLibrary = (): LibPic[] => LIB ?? []

// ---------- the matcher (libmatch.js) ----------

const HARD = new Set(['civic-government', 'safety-alerts', 'politics', 'true-crime', 'military', 'geopolitics', 'food-drink'])
// Oct 17 (Brian): civic and politics stories may take a picture flagged safe: 'process' (a ballot box, a polling place, an
// empty dais: no faces, no candidates); food stories, and restaurant stories under any topic, one flagged safe: 'mood' (a
// wine pour, a bar counter, a plate close up: never pretending to be the place). The rest of hard news stays without.
const NEVER = new Set(['safety-alerts', 'true-crime', 'military', 'geopolitics'])
const PROCESS = new Set(['civic-government', 'politics'])
const FOOD = 'food-drink'
const STOP = new Set(('the and for with from after this that into over under near its new set sets city county week weekend day days ' +
  'year years more most first last next our your their what when where who how why about than them they you not are was were will has ' +
  'have had been being off out per can may could would should also just still says said say plan plans get gets got make makes made ' +
  'two three four five six seven eight ten one percent up down part all any some other own back big small high low old local area ' +
  'generic photo image picture time people person thing way his her him she he it').split(' '))

export function stem(w: string): string {
  let s = w.toLowerCase()
  if (s.length > 4 && /ies$/.test(s)) s = s.slice(0, -3) + 'y'
  else for (const suf of ['ing', 'ed', 'es', 's']) if (s.endsWith(suf) && s.length - suf.length >= 3) { s = s.slice(0, -suf.length); break }
  if (s.length > 4 && s.endsWith('e')) s = s.slice(0, -1)   // price/pricing -> pric, but wine stays wine (not win)
  if (/([b-df-hj-np-tv-z])\1$/.test(s)) s = s.slice(0, -1)
  return s
}

export const words = (t: unknown): string[] =>
  (String(t ?? '').toLowerCase().replace(/[’']/g, '').match(/[a-z]{3,}/g) || [])
    .filter((w) => !STOP.has(w)).map(stem).filter((w) => w.length >= 3 && !STOP.has(w))

// the story's season, from a month or a holiday in its text; a picture tagged for another season is marked down
const MONTH: Record<string, string> = {
  jan: 'winter', feb: 'winter', mar: 'spring winter', apr: 'spring', may: 'spring', jun: 'summer spring', jul: 'summer',
  aug: 'summer', sep: 'fall summer', oct: 'fall', nov: 'fall winter', dec: 'winter', halloween: 'fall', thanksgiving: 'fall winter',
  christmas: 'winter', hanukkah: 'winter',
}
const seasonOf = (t: string): string[] | null => {
  const m = t.toLowerCase().match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d|\b(halloween|thanksgiving|christmas|hanukkah)\b/)
  return m ? MONTH[m[1] || m[2]].split(' ') : null
}

interface Doc { p: LibPic; strong: Set<string>; fit: Set<string>; weak: Set<string>; avoid: string[][]; phrases: string[][] }
interface Index { docs: Doc[]; idf: (w: string) => number; specific: number; rare: number }
let IX: Index | null = null

function index(): Index {
  if (IX) return IX
  const docs: Doc[] = (LIB ?? []).filter((p) => p.safe || (!HARD.has(p.topic) && p.tone !== 'serious')).map((p) => ({
    p,
    strong: new Set(words([...(p.kw || []), p.subtopic.replace(/-/g, ' ')].join(' '))),
    fit: new Set(words((p.fits || []).join(' '))),
    weak: new Set(words((p.shows || '') + ' ' + p.alt)),
    avoid: (p.avoid || []).map((a) => words(a)).filter((a) => a.length),
    phrases: [...(p.kw || []), ...(p.fits || [])].map(words).filter((a) => a.length >= 2 && a.length <= 3),
  }))
  const df: Record<string, number> = {}
  docs.forEach((d) => new Set([...d.strong, ...d.fit]).forEach((w) => { df[w] = (df[w] || 0) + 1 }))
  const N = docs.length, idf = (w: string) => Math.log((N + 1) / ((df[w] || 0) + 1))
  // "specific" = in about a dozen pictures or fewer
  return (IX = { docs, idf, specific: Math.log((N + 1) / 13), rare: Math.log((N + 1) / 6) })
}

const ALIAS: Record<string, string> = { sports: 'local-sports' }   // the story packs say "sports"; the library says local sports
const topicOf = (t: string | null | undefined): string => { const s = String(t ?? ''); return ALIAS[s] || BRIAN_TOPIC_IDS[s] || s }
/** NAMED: the headline names a subject only a handful of pictures show ("soccer"). Tuned Oct 17 by Brian. */
export const FLOOR = 14
export const FLOOR_NAMED = 9

interface H { id: string; topic: string; hl: string; dek: string; angle: string; label: string; search: string; see: string[] }
const toH = (s: PicStory): H => ({
  id: s.id ?? '', topic: topicOf(s.topic), hl: s.headline ?? '', dek: s.summary ?? '', angle: s.angle ?? '',
  label: s.label ?? '', search: s.search ?? '', see: s.see ?? [],
})
// libmatch.js joins with [].join(' '), which writes '' for a missing field
const j = (a: (string | undefined)[], sep = ' ') => a.map((x) => x ?? '').join(sep)

function rank(h: H, n?: number): PicMatch[] {
  if (!LIB) return []
  const { docs, idf, specific, rare } = index()
  const topic = topicOf(h.topic)
  const see = new Set(words(h.see.join(' ')))
  const head = new Set(words(j([h.hl, h.label])))   // the headline says what the story is about; the dek adds detail
  const text = new Set(words(j([h.hl, h.dek, h.angle, h.label, h.search])))   // search: a pack story's photo-search line
  const want = new Set([...see, ...text])
  const vocab = (w: string) => idf(w) < Math.log(docs.length + 1)   // a word some picture uses
  ;[...want].forEach((w) => {
    if (w.length >= 8 && !vocab(w)) for (let i = w.length - 4; i >= 4; i--) {
      const a = stem(w.slice(0, i)), b = stem(w.slice(i))
      if (vocab(a)) {
        const add = (x: string) => { want.add(x); if (head.has(w)) head.add(x) }
        add(a); if (vocab(b)) add(b); if (b === 'fest') add('festival'); break
      }
    }
  })
  const seq = words(j([h.see.join(' . '), h.hl, h.dek, h.search], ' . ')).join(' ')   // for phrases ("hot springs", "ski area")
  const when = seasonOf(j([h.hl, h.dek]))
  const seed = [...String(h.id || h.hl || '')].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 997, 0)
  const out: PicMatch[] = []
  docs.forEach((d, i) => {
    // an avoid entry fires on its own words, not the picture's subject ("hot springs burn injury" needs burn or injury)
    if (d.avoid.some((a) => { const own = a.filter((w) => !d.strong.has(w)); return own.length && own.filter((w) => want.has(w)).length >= Math.min(2, own.length) })) return
    let s = 0, sharp = false, named = false
    const hits: string[] = []
    want.forEach((w) => {
      const k0 = d.strong.has(w) ? 1 : d.fit.has(w) ? 0.6 : d.weak.has(w) ? 0.25 : 0
      if (!k0) return
      const v = idf(w) * k0 * (see.has(w) ? 1.6 : head.has(w) ? 1.3 : 0.75)
      s += v; hits.push(w)
      if (k0 >= 0.6 && idf(w) >= specific) sharp = true
      if (k0 === 1 && head.has(w) && idf(w) >= rare && d.p.topic === topic) named = true   // own topic only
    })
    d.phrases.forEach((ph) => {
      if ((' ' + seq + ' ').includes(' ' + ph.join(' ') + ' ')) { s += ph.reduce((a, w) => a + idf(w), 0) * 0.8; hits.push(ph.join(' ')); sharp = true }
    })
    if (!s) return
    if (d.p.topic === topic) s *= 1.25
    if (when && d.p.season && d.p.season !== 'any' && !when.includes(d.p.season)) s *= 0.45
    out.push({ own: d.p.topic === topic, id: d.p.id, url: libraryUrl(d.p.id), safe: d.p.safe || null, score: Math.round(s * 10) / 10, hits, sharp, named, tie: (seed + i * 7) % 13 })
  })
  out.sort((a, b) => b.score - a.score || (a.tie ?? 0) - (b.tie ?? 0))
  return out.slice(0, n || 5)
}

/** The top n pictures for a story, best first (for testing). Empty until the library loads. */
export function rankPhotos(story: PicStory, n?: number): PicMatch[] {
  return rank(toH(story), n)
}

// a restaurant story under another topic follows the food rule (the food has to look like the food): only a mood picture.
// Coffee shops, bakeries and food-truck pods keep theirs; the library shows those businesses as they are.
const EATERY = /\b(restaurant|eatery|diner|grill|burgers?|tacos?|taqueria|pizza|pizzeria|sushi|ramen|bbq|barbecue|chippy|fish and chips|steakhouse|bistro|fast[- ]food|street food|food hall|chick-fil-a)\b/i
// a mood picture that shows a particular dish or drink only goes with a story about it (Asian street food must not get tacos);
// a restaurant story that matches nothing specific takes a plain room or kitchen picture instead of none
const DISH: [RegExp, RegExp][] = [[/pizza/, /pizz/i], [/burger/, /burger/i], [/taco/, /taco|taqueria|mexican/i], [/salad/, /salad|farm.to.table|farmers? market/i],
  [/-pie-/, /\bpies?\b|dessert|bakery/i], [/wine/, /wine|winery|vineyard|tasting room/i], [/brewery|beer/, /beer|brew|taproom|\bales?\b|ipa/i],
  [/cocktail|bartender|-bar-/, /cocktail|\bbars?\b|lounge|speakeasy|bartender|pub|tavern/i], [/soup/, /soup|cafe|deli/i]]
const dishOk = (id: string, h: H) => { const d = DISH.find(([rx]) => rx.test(id)); return !d || d[1].test(j([h.hl, h.dek, h.see.length ? h.see.join(' ') : undefined])) }
const PLAIN_MOOD = ['food-restaurant-dining-generic-empty-room-golden-hour-01', 'food-restaurant-dining-generic-two-diners-window-from-behind-01',
  'food-kitchen-generic-chef-plating-microgreens-01', 'food-plated-generic-seasonal-dish-overhead-fork-01']
// a council, board or ballot story with no specific match takes a plain chamber or county-building picture
const PLAIN_PROCESS = ['civic-council-meeting-generic-empty-county-dais-01', 'civic-public-comment-generic-empty-podium-chamber-01',
  'civic-government-building-generic-county-building-steps-01']
const PROCESS_STORY = /\b(council|commission(ers?)?|board|county|city hall|vote[sd]?|voting|ballot|hearing|ordinance|levy|bond)\b/i

/**
 * The library photo for a story, or null (below the floor, hard news, or the
 * library not loaded yet). `shown`: ids already showing on the page; a fresh
 * match that clears the bar wins over a repeat (Brian's log #012).
 */
const GRIM = /\b(dies|died|death|dead|deadly|killed|kills|fatal(ly|ity|ities)?|crash(es|ed)?|shooting|shot|stabb(ing|ed)|arrest(s|ed)?|murder(ed)?|homicide|assault(ed)?|overdose|missing person)\b/i

export function pickPhoto(story: PicStory, shown?: ReadonlySet<string>): PicMatch | null {
  if (!LIB) return null
  const avoid = shown ?? new Set<string>()
  const h = toH(story)
  if (NEVER.has(h.topic)) return null
  // Our own guard on top of Brian's rules: a story about a death, a crash, a
  // shooting, a stabbing or an arrest never gets a stock photo, even when it
  // is filed under a soft topic by mistake. A wrong picture is worse than none.
  if (GRIM.test(`${h.hl} ${h.dek ?? ''}`)) return null
  const need = PROCESS.has(h.topic) ? 'process' : (h.topic === FOOD || EATERY.test(h.hl)) ? 'mood' : null
  const as = need === 'mood' ? { ...h, topic: FOOD } : h   // a restaurant headline is a food story here
  const top = rank(as, need ? 1e4 : 8).filter((r) => (!need || r.safe === need) && (need !== 'mood' || dishOk(r.id, h))).slice(0, 4)
  const seedOf = () => [...String(h.id || h.hl)].reduce((a, c) => a + c.charCodeAt(0), 0)
  const plain = (list = PLAIN_MOOD, kind: 'mood' | 'process' = 'mood'): PicMatch | null => {
    const ids = list.filter((id) => LIB!.some((p) => p.id === id)); if (!ids.length) return null
    const fresh = ids.filter((id) => !avoid.has(id)), pool = fresh.length ? fresh : ids, id = pool[seedOf() % pool.length]
    return { id, url: libraryUrl(id), safe: kind, score: 0, hits: ['plain'] }
  }
  let ok = top.filter((r) => r.score >= (r.named ? FLOOR_NAMED : FLOOR))   // each candidate against its own bar
  if (ok.some((r) => !avoid.has(r.id))) ok = ok.filter((r) => !avoid.has(r.id))   // #012: no repeat while another good match exists
  if (!ok.length) return need === 'mood' ? plain() : need === 'process' && PROCESS_STORY.test(h.hl) ? plain(PLAIN_PROCESS, 'process') : null
  // the story's own topic first: another topic's picture wins only when it is clearly better (a school named Mountain View
  // must not pull a wine-patio-with-a-view picture over the soccer one)
  const own = ok.find((r) => r.own)
  if (own && ok[0] !== own && ok[0].score < own.score * 1.5) ok = ok.filter((r) => r.own)
  // near-ties (within 8%) rotate by story so two stories on the same subject don't always share a picture
  const close = ok.filter((r) => r.score >= ok[0].score * 0.92)
  return close[seedOf() % close.length]
}
